import React from "react";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Line, Doughnut } from "react-chartjs-2";

ChartJS.register(
  ArcElement,
  BarElement,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  ChartDataLabels
);

const chartMap = {
  bar: Bar,
  line: Line,
  doughnut: Doughnut,
};

const GraphComponent = ({ type = "bar", data, options, graphHeading }) => {
  const ChartComponent = chartMap[type];

  if (!ChartComponent) return <div>Invalid Chart Type</div>;

  // Empty heading = bare chart only (used by dashboard donut)
  if (graphHeading === "") {
    return (
      <ChartComponent
        data={data}
        options={{ ...options, maintainAspectRatio: false }}
        plugins={[ChartDataLabels]}
      />
    );
  }

  const isAdminStats = graphHeading === "Admin Stats";

  // Keep Admin Stats simple
  if (isAdminStats) {
    return (
      <div className="admin_status">
        <div style={{ width: "100%" }} className="dashboard-text">
          <p style={{ color: "White", fontSize: "22px", alignContent: "left" }}>{graphHeading}</p>
        </div>
        <div className="donut-section">
          <ChartComponent
            data={data}
            options={{ ...options, maintainAspectRatio: false }}
            plugins={[ChartDataLabels]}
          />
        </div>
      </div>
    );
  }

  // Calculate total and max value for stats display
  const totalValue = data?.datasets?.[0]?.data?.reduce((a, b) => a + b, 0) || 0;
  const maxValue = data?.datasets?.[0]?.data ? Math.max(...data.datasets[0].data) : 0;
  const avgValue = totalValue && data?.datasets?.[0]?.data?.length
    ? Math.round(totalValue / data.datasets[0].data.length)
    : 0;
  const dataPointsCount = data?.labels?.length || 0;

  return (
    <div
      style={{
        background: 'linear-gradient(to bottom, #1e2530 0%, #151a24 100%)',
        borderRadius: '20px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        position: 'relative',
      }}
    >
      {/* Animated gradient line on top */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '3px',
        background: 'linear-gradient(90deg, #667eea 0%, #764ba2 25%, #f093fb 50%, #4facfe 75%, #00f2fe 100%)',
        backgroundSize: '200% 100%',
        animation: 'gradientMove 4s linear infinite',
      }} />

      <style>
        {`
          @keyframes gradientMove {
            0% { background-position: 0% 50%; }
            100% { background-position: 200% 50%; }
          }
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>

      {/* Header with Stats */}
      <div
        style={{
          padding: '24px 32px',
          background: 'rgba(30, 37, 48, 0.8)',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '20px',
        }}
      >
        {/* Title Section */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div style={{
            width: '50px',
            height: '50px',
            borderRadius: '14px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 20px rgba(102, 126, 234, 0.4)',
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
              <rect x="3" y="3" width="7" height="7" rx="1"></rect>
              <rect x="14" y="3" width="7" height="7" rx="1"></rect>
              <rect x="14" y="14" width="7" height="7" rx="1"></rect>
              <rect x="3" y="14" width="7" height="7" rx="1"></rect>
            </svg>
          </div>

          <div>
            <h3 style={{
              color: "#ffffff",
              fontSize: "24px",
              fontWeight: '700',
              margin: 0,
              marginBottom: '4px',
              letterSpacing: '-0.5px',
            }}>
              {graphHeading}
            </h3>
            <p style={{
              color: "rgba(255, 255, 255, 0.5)",
              fontSize: "13px",
              margin: 0,
              fontWeight: '500',
            }}>
              {dataPointsCount} data points tracked
            </p>
          </div>
        </div>

        {/* Mini Stats Cards */}
        <div style={{
          display: 'flex',
          gap: '12px',
          animation: 'fadeIn 0.6s ease-out',
        }}>
          {/* Total Card */}
          <div style={{
            padding: '12px 18px',
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
            borderRadius: '12px',
            border: '1px solid rgba(102, 126, 234, 0.3)',
            minWidth: '90px',
          }}>
            <div style={{
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '11px',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '4px',
            }}>
              Total
            </div>
            <div style={{
              color: '#667eea',
              fontSize: '20px',
              fontWeight: '700',
            }}>
              {totalValue.toLocaleString()}
            </div>
          </div>

          {/* Average Card */}
          <div style={{
            padding: '12px 18px',
            background: 'linear-gradient(135deg, rgba(79, 172, 254, 0.15) 0%, rgba(0, 242, 254, 0.15) 100%)',
            borderRadius: '12px',
            border: '1px solid rgba(79, 172, 254, 0.3)',
            minWidth: '90px',
          }}>
            <div style={{
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '11px',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '4px',
            }}>
              Average
            </div>
            <div style={{
              color: '#4facfe',
              fontSize: '20px',
              fontWeight: '700',
            }}>
              {avgValue.toLocaleString()}
            </div>
          </div>

          {/* Peak Card */}
          <div style={{
            padding: '12px 18px',
            background: 'linear-gradient(135deg, rgba(240, 147, 251, 0.15) 0%, rgba(245, 87, 108, 0.15) 100%)',
            borderRadius: '12px',
            border: '1px solid rgba(240, 147, 251, 0.3)',
            minWidth: '90px',
          }}>
            <div style={{
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '11px',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '4px',
            }}>
              Peak
            </div>
            <div style={{
              color: '#f093fb',
              fontSize: '20px',
              fontWeight: '700',
            }}>
              {maxValue.toLocaleString()}
            </div>
          </div>
        </div>
      </div>

      {/* Chart Area */}
      <div
        style={{
          flex: 1,
          padding: '28px 32px 32px 32px',
          background: 'linear-gradient(to bottom, #1a212e 0%, #151a24 100%)',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div style={{
          position: 'absolute', top: '-50px', right: '-50px',
          width: '200px', height: '200px',
          background: 'radial-gradient(circle, rgba(102, 126, 234, 0.1) 0%, transparent 70%)',
          filter: 'blur(40px)', pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', bottom: '-50px', left: '-50px',
          width: '200px', height: '200px',
          background: 'radial-gradient(circle, rgba(79, 172, 254, 0.08) 0%, transparent 70%)',
          filter: 'blur(40px)', pointerEvents: 'none',
        }} />

        <div style={{
          position: 'relative', width: '100%', height: '100%',
          minHeight: '500px', zIndex: 1,
        }}>
          <ChartComponent
            data={data}
            options={{ ...options, responsive: true, maintainAspectRatio: false }}
            plugins={[ChartDataLabels]}
          />
        </div>
      </div>
    </div>
  );
};

export default GraphComponent;